import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, User, List, X, CaretDown } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';

const darkHeroPages = ['/', '/about', '/partner', '/impact', '/faq', '/legal'];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCollectionOpen, setIsCollectionOpen] = useState(false);
  const { user, logout } = useAuth();
  const { cart } = useCart();
  const location = useLocation();

  const hasDarkHero = darkHeroPages.some(p => location.pathname === p || location.pathname.startsWith(p + '/'));
  const useWhiteText = hasDarkHero && !isScrolled;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const cartItemCount = cart?.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;

  const navTextClass = useWhiteText
    ? 'text-white/80 hover:text-white'
    : 'text-[#7a5e75] hover:text-[#641b4d]';
  const logoTextClass = useWhiteText ? 'text-white' : 'text-[#641b4d]';
  const iconClass = useWhiteText ? 'text-white' : 'text-[#641b4d]';

  return (
    <header
      data-testid="main-header"
      className={`fixed top-8 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'backdrop-blur-xl bg-[#F4F4F4]/80 border-b border-[#E5E2DC]' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="block -my-4">
          <img
            src={hasDarkHero && !isScrolled ? '/logos/veveru_white.svg' : '/logos/veveru_purple.svg'}
            alt="Veveru"
            className="h-20 md:h-24 w-auto"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <Link to="/about" data-testid="about-nav-link" className={`text-sm uppercase tracking-wider transition-colors ${navTextClass}`}>
            About Us
          </Link>

          {/* Our Collection dropdown */}
          <div className="relative group">
            <button
              data-testid="collection-nav-link"
              className={`text-sm uppercase tracking-wider transition-colors flex items-center gap-1 ${navTextClass}`}
            >
              Our Collection
              <CaretDown size={14} weight="bold" />
            </button>
            <div className="absolute left-0 top-full mt-2 w-48 bg-white border border-[#E5E2DC] shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
              <Link to="/products" data-testid="products-nav-link" className="block px-5 py-3 text-sm text-[#641b4d] hover:bg-[#F4F4F4] transition-colors">
                Our Products
              </Link>
              <Link to="/custom-inquiry" data-testid="custom-inquiry-nav-link" className="block px-5 py-3 text-sm text-[#641b4d] hover:bg-[#F4F4F4] transition-colors">
                Custom Order
              </Link>
              <Link to="/bulk-inquiry" data-testid="bulk-inquiry-nav-link" className="block px-5 py-3 text-sm text-[#641b4d] hover:bg-[#F4F4F4] transition-colors">
                Bulk Inquiry
              </Link>
              <Link to="/faq" data-testid="faq-nav-link" className="block px-5 py-3 text-sm text-[#641b4d] hover:bg-[#F4F4F4] transition-colors">
                FAQs
              </Link>
              <Link to="/legal" data-testid="legal-nav-link" className="block px-5 py-3 text-sm text-[#641b4d] hover:bg-[#F4F4F4] transition-colors">
                Legal
              </Link>
            </div>
          </div>

          <Link to="/impact" data-testid="impact-nav-link" className={`text-sm uppercase tracking-wider transition-colors ${navTextClass}`}>
            Impact
          </Link>
          <Link to="/partner" data-testid="partner-nav-link" className={`text-sm uppercase tracking-wider transition-colors ${navTextClass}`}>
            Partner With Us
          </Link>
        </nav>

        <div className="flex items-center gap-4">
          <Link to="/cart" data-testid="cart-icon-link" className="relative">
            <ShoppingCart size={24} weight="light" className="text-[#641b4d]" />
            {cartItemCount > 0 && (
              <span data-testid="cart-count-badge" className="absolute -top-2 -right-2 bg-[#bc64aa] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {cartItemCount}
              </span>
            )}
          </Link>

          {user ? (
            <div className="relative group">
              <button data-testid="user-menu-button" className="flex items-center gap-2">
                <User size={24} weight="light" className={`transition-colors ${iconClass}`} />
              </button>
              <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-[#E5E2DC] shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                <Link to="/orders" data-testid="orders-link" className="block px-4 py-3 text-sm text-[#641b4d] hover:bg-[#F4F4F4]">
                  My Orders
                </Link>
                {user.is_admin && (
                  <Link to="/admin" data-testid="admin-link" className="block px-4 py-3 text-sm text-[#641b4d] hover:bg-[#F4F4F4]">
                    Admin Dashboard
                  </Link>
                )}
                <button
                  data-testid="logout-button"
                  onClick={logout}
                  className="block w-full text-left px-4 py-3 text-sm text-[#641b4d] hover:bg-[#F4F4F4]"
                >
                  Logout
                </button>
              </div>
            </div>
          ) : (
            <Link to="/auth" data-testid="login-link">
              <User size={24} weight="light" className={`transition-colors ${iconClass}`} />
            </Link>
          )}

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden"
          >
            {isMobileMenuOpen ? <X size={24} /> : <List size={24} />}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div data-testid="mobile-menu" className="md:hidden bg-white border-t border-[#E5E2DC]">
          <nav className="flex flex-col p-4">
            <Link to="/about" className="py-3 text-sm uppercase tracking-wider" onClick={() => setIsMobileMenuOpen(false)}>
              About Us
            </Link>
            <button
              onClick={() => setIsCollectionOpen(!isCollectionOpen)}
              className="py-3 text-sm uppercase tracking-wider flex items-center justify-between"
            >
              Our Collection
              <CaretDown size={14} weight="bold" className={`transition-transform ${isCollectionOpen ? 'rotate-180' : ''}`} />
            </button>
            {isCollectionOpen && (
              <div className="pl-4 border-l border-[#E5E2DC] ml-2">
                <Link to="/products" className="py-2 text-sm text-[#7a5e75] block" onClick={() => setIsMobileMenuOpen(false)}>
                  Our Products
                </Link>
                <Link to="/custom-inquiry" className="py-2 text-sm text-[#7a5e75] block" onClick={() => setIsMobileMenuOpen(false)}>
                  Custom Order
                </Link>
                <Link to="/bulk-inquiry" className="py-2 text-sm text-[#7a5e75] block" onClick={() => setIsMobileMenuOpen(false)}>
                  Bulk Inquiry
                </Link>
                <Link to="/faq" className="py-2 text-sm text-[#7a5e75] block" onClick={() => setIsMobileMenuOpen(false)}>
                  FAQs
                </Link>
                <Link to="/legal" className="py-2 text-sm text-[#7a5e75] block" onClick={() => setIsMobileMenuOpen(false)}>
                  Legal
                </Link>
              </div>
            )}
            <Link to="/impact" className="py-3 text-sm uppercase tracking-wider" onClick={() => setIsMobileMenuOpen(false)}>
              Impact
            </Link>
            <Link to="/partner" className="py-3 text-sm uppercase tracking-wider" onClick={() => setIsMobileMenuOpen(false)}>
              Partner With Us
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;